import { useState } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Icon } from '@/shared/icons';
import { useGenerateMcpToken } from './useMcpToken';
import type { CreateMcpTokenResponse } from '@troia-v3/types';

interface GerarTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasExisting: boolean;
}

type Step = 'confirm' | 'generate' | 'result';

export const GerarTokenModal: React.FC<GerarTokenModalProps> = ({
  isOpen,
  onClose,
  hasExisting,
}) => {
  const [step, setStep] = useState<Step>(() =>
    hasExisting ? 'confirm' : 'generate'
  );
  const [generated, setGenerated] = useState<CreateMcpTokenResponse | null>(null);
  const [copied, setCopied] = useState<'url' | 'json' | null>(null);

  const generate = useGenerateMcpToken();

  const handleOpen = (): void => {
    // Reset state when modal opens
    setStep(hasExisting ? 'confirm' : 'generate');
    setGenerated(null);
    setCopied(null);
  };

  // Reset state when modal becomes visible (hasExisting may change between opens)
  if (isOpen && generated === null && step !== (hasExisting ? 'confirm' : 'generate') && step !== 'generate' && step !== 'result') {
    handleOpen();
  }

  const handleClose = (): void => {
    setStep(hasExisting ? 'confirm' : 'generate');
    setGenerated(null);
    setCopied(null);
    onClose();
  };

  const handleGenerate = async (): Promise<void> => {
    const result = await generate.mutateAsync();
    setGenerated(result);
    setStep('result');
  };

  const handleCopy = (type: 'url' | 'json', text: string): void => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="xl">
      <div data-testid="gerar-token-modal">
      {/* F74: título 18px via prop tipada do ModalHeader */}
      <ModalHeader
        title={step === 'result' ? 'Token gerado com sucesso' : 'Gerar token MCP'}
        onClose={handleClose}
      >
        <Icon name="link" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
      </ModalHeader>

      <ModalContent>
        {/* Passo 1: confirmação se já existe token */}
        {step === 'confirm' && (
          <div className="space-y-4" data-testid="confirm-step">
            <div
              className="flex items-start gap-3 p-3 rounded-lg border"
              style={{ backgroundColor: '#fef3c7', borderColor: '#fcd34d' }}
            >
              <Icon name="alert-triangle" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <p className="text-sm" style={{ color: '#92400e' }}>
                Você já tem um token ativo. Gerar um novo vai revogar o token anterior imediatamente. Ferramentas que usam o token atual vão parar de funcionar.
              </p>
            </div>
            <p className="text-sm" style={{ color: '#6b7280' }}>
              Deseja continuar e gerar um novo token?
            </p>
          </div>
        )}

        {/* Passo 2: gerar */}
        {step === 'generate' && (
          <div className="space-y-4" data-testid="generate-step">
            <p className="text-sm" style={{ color: '#6b7280' }}>
              Vamos gerar uma nova conexão MCP para sua conta TROIA. O token completo será exibido{' '}
              <strong style={{ color: '#1e293b' }}>uma única vez</strong> — copie-o antes de fechar.
            </p>
            <div
              className="flex items-start gap-3 p-3 rounded-lg border"
              style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
            >
              <Icon name="info" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
              <p className="text-sm" style={{ color: '#15803d' }}>
                O JSON gerado pode ser colado diretamente no Claude Desktop para configurar a conexão automaticamente.
              </p>
            </div>
          </div>
        )}

        {/* Passo 3: resultado */}
        {step === 'result' && generated && (
          <div className="space-y-4" data-testid="result-step">
            <div
              className="flex items-start gap-3 p-3 rounded-lg border"
              style={{ backgroundColor: '#fef3c7', borderColor: '#fcd34d' }}
            >
              <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                Este token aparece <strong>uma única vez</strong>. Copie agora — não será possível visualizá-lo novamente.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>
                URL do MCP
              </label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Input
                    readOnly
                    value={generated.mcpUrl}
                    className="font-mono"
                    style={{ backgroundColor: '#f8fafc' }}
                    data-testid="mcp-url-input"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={copied === 'url' ? 'check' : 'copy'}
                  onClick={() => handleCopy('url', generated.mcpUrl)}
                  data-testid="copiar-url-btn"
                >
                  {copied === 'url' ? 'Copiado!' : 'Copiar'}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#374151' }}>
                Configuração JSON{' '}
                <span className="font-normal" style={{ color: '#9ca3af' }}>
                  (cole no Claude Desktop)
                </span>
              </label>
              <Textarea
                readOnly
                value={generated.configJson}
                rows={8}
                className="font-mono resize-none"
                style={{ backgroundColor: '#f8fafc' }}
                data-testid="config-json-textarea"
              />
              <Button
                variant="secondary"
                size="sm"
                icon={copied === 'json' ? 'check' : 'copy'}
                onClick={() => handleCopy('json', generated.configJson)}
                className="mt-2"
                data-testid="copiar-json-btn"
              >
                {copied === 'json' ? 'Copiado!' : 'Copiar JSON'}
              </Button>
            </div>
          </div>
        )}
      </ModalContent>

      <ModalFooter>
        {step === 'confirm' && (
          <>
            {/* F71: Cancelar = secondary (bordado) */}
            <Button variant="secondary" onClick={handleClose} data-testid="cancelar-btn">
              Cancelar
            </Button>
            <Button
              variant="error"
              onClick={() => setStep('generate')}
              data-testid="confirmar-revogacao-btn"
            >
              Sim, revogar e gerar novo
            </Button>
          </>
        )}

        {step === 'generate' && (
          <>
            <Button variant="secondary" onClick={handleClose} data-testid="cancelar-generate-btn">
              Cancelar
            </Button>
            <Button
              onClick={() => void handleGenerate()}
              loading={generate.isPending}
              icon="zap"
              data-testid="gerar-btn"
            >
              Gerar token
            </Button>
          </>
        )}

        {step === 'result' && (
          <Button onClick={handleClose} data-testid="fechar-btn">
            Concluir
          </Button>
        )}
      </ModalFooter>
      </div>
    </Modal>
  );
};
